import type { Annotation } from '@studio/design-system/video-annotation';
import { TrackingSample } from '@tryiris-ai/common.protojs/autotracking/v2/annotation';
import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { noop } from 'lodash-es';
import { autoTrackingNotificationSubjectWithReservation } from '../utils.js';
import type { AutoTrackingStateManager } from './state-manager.js';

import { annotationSubjectForAgentCameraAndWorker } from './utils.js';
import type { Handlers } from '#~/agent/types.js';
import type { ByteId } from '#~/api/index.js';
import { toBase64FromId, toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { Subscription } from 'nats.ws';
import type { Agent } from '@studio/models/agent';
import { nats } from '#~/nats/index.js';

/*
let HACK_CONN: NatsConnection | undefined;

const c = async (): Promise<void> => {
  try {
    HACK_CONN = await connect({ servers: ['wss://hunters-local-test.tryiris.ai:8080'] });
  } catch (error) {
    console.log('error', error);
  }
};

void c();
*/

// Temporary hack to clear annotations when tracking is lost.
const HACK_clearAnnotationsTimeouts: Record<string, number> = {};

/** Annotation Manager. */
export class AutoTrackingAnnotationManager {
  readonly #annotations = new Map<string, Annotation[]>();
  readonly #handleAutoTrackingAnnotationMutation: Handlers['handleAutoTrackingAnnotationMutation'];
  readonly #reservationNotificationSubjectSubscriptions = new Map<string, Subscription>();
  readonly #stateManager: AutoTrackingStateManager;
  readonly #agentId: Agent['id'];

  /**
   * Creates a new annotation manager.
   *
   * @param conn - The nats connection to use.
   * @param stateManager - The subscription manager to use.
   * @param handleAutoTrackingAnnotationMutation - The function to call when an annotation mutation
   *   is received.
   */
  public constructor(
    agentId: Agent['id'],
    stateManager: AutoTrackingStateManager,
    handleAutoTrackingAnnotationMutation: Handlers['handleAutoTrackingAnnotationMutation']
  ) {
    this.#agentId = agentId;
    this.#handleAutoTrackingAnnotationMutation = handleAutoTrackingAnnotationMutation;
    this.#stateManager = stateManager;
  }

  /**
   * Ensures that we have the correct subscriptions for the current reservations. This is called
   * when a mutation is made to the reservation state.
   *
   * @param reservations - The reservations to check.
   */
  public checkNotificationSubscriptions(
    reservations: Record<string, AutoTrackingReservation>
  ): void {
    // Loop over all reservations
    for (const reservation of Object.values(reservations)) {
      const { cameraId, workerId } = reservation;

      if (!cameraId || !workerId) {
        continue;
      }
      /*
      const stateId = autoTrackingNotificationSubjectWithReservation({
        agentId: reservation.agentId,
        reservationId: reservation.reservationId,
        workerId: reservation.workerId
      });

      if (!stateId) {
        return;
      }
*/

      const subject = annotationSubjectForAgentCameraAndWorker(
        reservation.agentId!,
        cameraId,
        reservation.workerId!
      );

      this.#listenForNotifications(cameraId, subject);
    }

    // Loop over all subscriptions
    for (const [key] of this.#reservationNotificationSubjectSubscriptions) {
      // If the subscription is not for a reservation, unsubscribe
      if (!reservations[key]) {
        this.#unsubscribeFromNotifications(key);
      }

      const reservation = reservations[key];

      const stateId = autoTrackingNotificationSubjectWithReservation({
        agentId: reservation?.agentId,
        reservationId: reservation?.reservationId,
        workerId: reservation?.workerId
      });

      if (!stateId) {
        this.#unsubscribeFromNotifications(key);
      }
    }
  }

  /**
   * Gets the annotations for the camera.
   *
   * @param id - The id of the camera to get the annotations for.
   * @returns The annotations.
   */
  public getAnnotationsForCameraId(id: ByteId): Annotation[] {
    return this.#annotations.get(toHexFromId(id)!) ?? [];
  }

  /**
   * Clears the annotations for the camera.
   *
   * @param id - The id of the camera to clear the annotations for.
   */
  #clearAnnotationsForCameraId(id: ByteId): void {
    this.#annotations.delete(toHexFromId(id)!);
    this.#handleAutoTrackingAnnotationMutation(this.#stateManager.agentId, id, [])?.catch(noop);
  }

  /**
   * Handles a notification for a camera id.
   *
   * @param cameraId - The camera id to handle the notification for.
   * @param message - The message to handle.
   */
  #handleNotificationForCameraId(cameraId: ByteId, message: ByteId): void {
    const notification = TrackingSample.decode(message);

    const { subjects, target } = notification;

    const targetId = target?.id;

    if (targetId) {
      const existingSubject = subjects.find(
        (subject) => toHexFromId(subject.id) === toHexFromId(targetId)
      );

      if (existingSubject === undefined) {
        subjects.push(target);
      } else {
        const index = subjects.indexOf(existingSubject);
        subjects[index] = target;
        //TODO is it a bug we never arrive here?
      }
    }

    const annotations = subjects.map(
      ({ height, id, width, x, y }): Annotation => ({
        color: toHexFromId(targetId) === toHexFromId(id) ? 'green' : 'white',
        height: height!,
        id: id!,
        label: `Subject ${toBase64FromId(id)!.slice(0, 8)}`,
        width: width!,
        x: x!,
        y: y!
      })
    );

    this.#annotations.set(toHexFromId(cameraId)!, annotations);

    this.#handleAutoTrackingAnnotationMutation(
      this.#stateManager.agentId,
      cameraId,
      annotations
    )?.catch(noop);

    clearTimeout(HACK_clearAnnotationsTimeouts[toHexFromId(cameraId)!]);

    HACK_clearAnnotationsTimeouts[toHexFromId(cameraId)!] = window.setTimeout(() => {
      this.#clearAnnotationsForCameraId(cameraId);
    }, 1000);
  }

  subscriptionCancelationTimeouts: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Listens for notifications for a camera id.
   *
   * @param cameraId - The camera id to listen for notifications for.
   * @param subject - The subject to listen for notifications on.
   */
  async #listenForNotifications(cameraId: ByteId, partSubject: string): Promise<void> {
    if (this.subscriptionCancelationTimeouts.has(toHexFromId(cameraId)!)) {
      clearTimeout(this.subscriptionCancelationTimeouts.get(toHexFromId(cameraId)!)!);
      this.subscriptionCancelationTimeouts.delete(toHexFromId(cameraId)!);
    }

    const subscription = this.#reservationNotificationSubjectSubscriptions.get(
      toHexFromId(cameraId)!
    );

    const subject = `${partSubject}.tracked`;

    if (subscription && subscription.getSubject() === subject) {
      return;
    }

    if (subscription) {
      subscription.unsubscribe();
      this.#reservationNotificationSubjectSubscriptions.delete(toHexFromId(cameraId)!);
    }

    const subj = nats.agentConnection(this.#agentId).subscribe(subject, {
      callback: (error, message) => {
        if (error) {
          throw error;
        }

        this.#handleNotificationForCameraId(cameraId, message.data);
      }
    });

    this.#reservationNotificationSubjectSubscriptions.set(toHexFromId(cameraId)!, subj);

    /*
    if(HACK_CONN === undefined) {
      console.log('HACK_CONN is undefined');
      return;
    }

    console.log('>>', subject, HACK_CONN);

    this.#reservationNotificationSubjectSubscriptions.set(
      toHexFromId(cameraId)!,
      HACK_CONN.subscribe('direct.authenticated.36d85c8370d94a3c8349d28905600b7a.at_override', {
        callback: (error, message) => {
          if (error) {
            throw error;
          }

          this.#handleNotificationForCameraId(cameraId, message.data);
        }
      })
    );
  */
  }

  /**
   * Unsubscribes from notifications for a camera id.
   *
   * @param cameraId - The camera id to unsubscribe from.
   */
  #unsubscribeFromNotifications(cameraId: string): void {
    // @todo: This logic may be broken and it's not cleaning up
    const timeout = setTimeout(() => {
      const subscription = this.#reservationNotificationSubjectSubscriptions.get(cameraId);

      if (subscription) {
        subscription.unsubscribe();
        this.#reservationNotificationSubjectSubscriptions.delete(cameraId);
        this.#clearAnnotationsForCameraId(toIdFromHex(cameraId)!);
      }
    }, 5000);

    if (this.subscriptionCancelationTimeouts.has(cameraId)) {
      clearTimeout(this.subscriptionCancelationTimeouts.get(cameraId)!);
    }

    this.subscriptionCancelationTimeouts.set(cameraId, timeout);
  }
}
